import { cloneElement, isValidElement, useId } from 'react'
import styles from './Forms.module.css'
import { FieldLabel, HintText } from '../../boardui/Fields'

export function Field({
  id,
  label,
  labelExtra,
  help,
  error,
  required = false,
  optional = false,
  children,
  className = '',
}) {
  const generatedId = useId()
  const controlId = (isValidElement(children) && children.props.id) || id || `field-${generatedId}`
  const helpId = help ? `${controlId}-help` : undefined
  const errorId = error ? `${controlId}-error` : undefined
  const describedBy = [errorId, helpId].filter(Boolean).join(' ') || undefined
  const control = isValidElement(children)
    ? cloneElement(children, {
      id: children.props.id || controlId,
      required: children.props.required ?? required,
      'aria-invalid': children.props['aria-invalid'] ?? (error ? true : undefined),
      'aria-describedby': [children.props['aria-describedby'], describedBy].filter(Boolean).join(' ') || undefined,
    })
    : children

  return (
    <div className={`${styles.field} ${className}`.trim()}>
      {label ? (
        <div className={styles.labelRow}>
          <FieldLabel htmlFor={controlId} required={required}>{label}</FieldLabel>
          {labelExtra || (optional ? <span className={styles.optional}>Optional</span> : null)}
        </div>
      ) : null}
      {control}
      {error ? <HintText id={errorId} invalid role="alert">{error}</HintText> : null}
      {help ? <HintText id={helpId}>{help}</HintText> : null}
    </div>
  )
}

// Grouped controls (chips, checkbox lists, min/max pairs) take their name from
// a legend: a <label> can only point at one labelable element, so wrapping a
// group in Field leaves assistive technology with no heading for it.
export function Fieldset({
  label,
  labelExtra,
  help,
  error,
  required = false,
  children,
  className = '',
}) {
  const id = useId()
  const helpId = help ? `${id}-help` : undefined
  const errorId = error ? `${id}-error` : undefined
  const describedBy = [errorId, helpId].filter(Boolean).join(' ') || undefined

  return (
    <fieldset className={`${styles.field} ${styles.fieldset} ${className}`.trim()} aria-describedby={describedBy}>
      <legend className={styles.legend}>
        <span className={styles.labelRow}>
          <span className={styles.label}>
            {label}{required ? <span className={styles.required} aria-hidden="true">*</span> : null}
          </span>
          {labelExtra}
        </span>
      </legend>
      {children}
      {error ? <HintText id={errorId} invalid role="alert">{error}</HintText> : null}
      {help ? <HintText id={helpId}>{help}</HintText> : null}
    </fieldset>
  )
}
